import React from 'react';
import { CalendarDays, TrendingUp, Percent, Clock, Car, PiggyBank, AlertCircle, Shield, DollarSign, Repeat, FileText } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета автокредита
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  // Расчет процента переплаты от суммы кредита
  const overpaymentPercent = (results.totalAdditionalCosts / results.loanAmount) * 100;
  
  // Рассчитываем эффективную ставку (ПСК)
  // Это грубая оценка, так как точный расчет ПСК требует сложного алгоритма
  const effectiveRate = (overpaymentPercent / results.loanTermYears);

  return (
    <ToolResult title="Результаты расчета автокредита">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Ежемесячный платеж */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <CalendarDays className="w-4 h-4"/>
            Ежемесячный платеж по кредиту
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {/* Показываем диапазон для дифференцированных платежей, иначе первый платеж */}
              {Math.abs(results.firstMonthlyPayment - results.lastMonthlyPayment) > 0.01
                ? `${formatCurrency(results.firstMonthlyPayment)} → ${formatCurrency(results.lastMonthlyPayment)}`
                : formatCurrency(results.firstMonthlyPayment)
              }
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            {Math.abs(results.firstMonthlyPayment - results.lastMonthlyPayment) > 0.01 
              ? 'Сумма уменьшается с течением времени (дифференцированный платеж)' 
              : 'Фиксированный платеж на весь срок (аннуитетный)'}
          </p>
          
          {results.averageInsurancePayment > 0 && (
            <div className="flex items-center mt-2 text-xs text-indigo-600 dark:text-indigo-400">
              <Shield className="w-3.5 h-3.5 mr-1" />
              <span>+ страховка: ~{formatCurrency(results.averageInsurancePayment)}/мес.</span>
            </div>
          )}
        </div>

        {/* Переплата и полная стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-900/30 dark:to-orange-900/30 p-5 border border-rose-100 dark:border-rose-800/50 shadow-sm">
          <h3 className="text-base font-medium text-rose-700 dark:text-rose-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Переплата по автокредиту
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-rose-800 dark:text-rose-200" aria-live="polite">
              {formatCurrency(results.totalAdditionalCosts)}
            </span>
            <span className="text-sm font-medium text-rose-600 dark:text-rose-400">
              ({formatNumber(overpaymentPercent, 1)}% от суммы)
            </span>
          </div>
          <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
            Включает проценты
            {results.totalInsurance > 0 ? ', страховку' : ''}
            {results.totalAdditionalFees > 0 ? ' и дополнительные сборы' : ''}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Стоимость автомобиля" 
          value={formatCurrency(results.carPrice)} 
          color="indigo" 
          icon={<Car className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Первоначальный взнос" 
          value={`${formatCurrency(results.downPayment)} (${formatNumber(results.downPaymentPercent, 1)}%)`} 
          color="green" 
          icon={<DollarSign className="w-4 h-4"/>}
        />
        
        {results.tradeInDiscount > 0 && (
          <ResultItem 
            label="Стоимость Trade-In" 
            value={formatCurrency(results.tradeInDiscount)} 
            color="blue" 
            icon={<Repeat className="w-4 h-4"/>}
          />
        )}
        
        <ResultItem 
          label="Сумма кредита" 
          value={formatCurrency(results.loanAmount)} 
          color="indigo" 
          icon={<DollarSign className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Процентная ставка" 
          value={`${formatNumber(results.interestRate, 2)}% годовых`} 
          color="orange" 
          icon={<Percent className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Срок кредита" 
          value={`${results.loanTermYears} ${formatYearsLabel(results.loanTermYears)} (${results.loanTermMonths} мес.)`} 
          icon={<Clock className="w-4 h-4"/>} 
        />
        
        {results.effectiveTermMonths !== results.loanTermMonths && (
          <ResultItem 
            label="Фактический срок погашения" 
            value={`${Math.floor(results.effectiveTermMonths / 12)} ${formatYearsLabel(Math.floor(results.effectiveTermMonths / 12))} ${results.effectiveTermMonths % 12} мес.`} 
            color="green"
            icon={<Clock className="w-4 h-4"/>} 
          />
        )}

        <ResultItem 
          label="Тип платежа" 
          value={formData.paymentType === 'annuity' ? 'Аннуитетный (равными платежами)' : 'Дифференцированный (уменьшающийся)'} 
        />
        
        <ResultItem 
          label="Сумма выплат по кредиту" 
          value={formatCurrency(results.loanAmount + results.totalInterest)} 
        />
        
        <ResultItem 
          label="Общая сумма всех платежей" 
          value={formatCurrency(results.totalPayment)} 
          color="rose"
        />
        
        <ResultItem 
          label="Переплата по процентам" 
          value={formatCurrency(results.totalInterest)} 
          color="orange" 
        />
        
        {results.totalInsurance > 0 && (
          <ResultItem 
            label="Расходы на страхование" 
            value={formatCurrency(results.totalInsurance)} 
            color="green" 
            icon={<Shield className="w-4 h-4"/>} 
          />
        )}
        
        {results.totalAdditionalFees > 0 && (
          <ResultItem 
            label="Дополнительные сборы" 
            value={formatCurrency(results.totalAdditionalFees)} 
            color="amber" 
            icon={<FileText className="w-4 h-4"/>} 
          />
        )}
        
        {results.totalEarlyRepayment > 0 && (
          <ResultItem 
            label="Досрочное погашение (всего)" 
            value={formatCurrency(results.totalEarlyRepayment)} 
            color="green" 
            icon={<PiggyBank className="w-4 h-4"/>} 
          />
        )}

        {/* Информация о ПСК */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Полная стоимость кредита (ПСК)</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                ПСК ≈ <span className="font-medium text-orange-600 dark:text-orange-400">{formatNumber(effectiveRate, 2)}% годовых</span>. 
                Реальная стоимость кредита с учетом всех платежей, включая страховки и дополнительные сборы.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const formatYearsLabel = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

export default ResultsOverview;